// elements/theming/design.ts
import { BehaviorSubject } from "rxjs";
import { ColorScheme } from "./coloring-system.js";
import { Spacing } from "./spacings.js";
var spacing = {
  xxs: Spacing.make(4, "px"),
  xs: Spacing.make(8, "px"),
  sm: Spacing.make(12, "px"),
  md: Spacing.make(20, "px"),
  lg: Spacing.make(32, "px"),
  xl: Spacing.make(48, "px"),
  xxl: Spacing.make(96, "px")
};
var breakPoints = {
  0: 414,
  1: 576,
  2: 768,
  3: 992,
  4: 1300,
  5: 1600,
  6: Infinity
};
var colors = {
  blue: "#00BCD4",
  green: "#46b46f",
  red: "#F04747",
  orange: "#ffa500",
  white: "#FFFFFF",
  grey: "#666688",
  black: "#36393f"
};
var DesignSystem = class {
  static {
    this.ready = new BehaviorSubject(false);
  }
  static {
    this.themes = {
      default: {
        spacing,
        baseFontSize: 14,
        baseFontSizePrint: 9.8,
        fontFamily: "Maven Pro",
        fontFamilyHeading: "Lato",
        colors: new ColorScheme({
          primary: colors.white,
          secondary: colors.grey,
          accent: colors.blue,
          neutral: colors.grey,
          tone: {
            active: colors.blue,
            success: colors.green,
            warning: colors.orange,
            error: colors.red,
            subtle: colors.grey,
            placeholder: colors.grey
          }
        }),
        borders: {},
        sizes: {
          borderRadius: "4px",
          borderRadiusSecondary: "2px",
          maxReadableWidth: "500px",
          maxPageWidth: "1080px",
          breakPoints
        },
        defaults: {
          placeholderMode: "inline-floating"
        }
      }
    };
  }
  static {
    this._prefix = "x";
  }
  static get prefix() {
    if (!this._prefix)
      throw new Error("You need to set this design systems prefix before rendering anything");
    return this._prefix;
  }
  static set prefix(prefix) {
    if (this._prefix && this._prefix !== "x")
      throw new Error("Prefix can only be set once");
    this._prefix = prefix;
    if (this._currentTheme.getValue())
      this.ready.next(true);
  }
  static setTheme(themeName, design) {
    this.themes[themeName] = design;
  }
  static getTheme(themeName) {
    return this.themes[themeName];
  }
  static useTheme(themeName) {
    console.log("use theme", themeName);
    const theme = this.themes[themeName];
    if (!theme)
      throw new Error(`Theme '${themeName}' was not registered before using it. Call DesignSystem.setTheme first!`);
    this._currentTheme.next(theme);
    this._currentThemeKey.next(themeName);
    if (this._prefix !== void 0)
      this.ready.next(true);
  }
  static extendBase(themeOverrides, colorOverride) {
    return {
      ...this.themes.default,
      ...themeOverrides,
      spacing: {
        ...this.themes.default.spacing,
        ...themeOverrides.spacing
      },
      colors: new ColorScheme({
        primary: colors.white,
        secondary: colors.grey,
        accent: colors.blue,
        neutral: colors.grey,
        ...colorOverride,
        tone: {
          active: colors.blue,
          success: colors.green,
          warning: colors.orange,
          error: colors.red,
          subtle: colors.grey,
          ...colorOverride.tone
        }
      }),
      borders: {
        ...this.themes.default.borders,
        ...themeOverrides.borders
      },
      sizes: {
        ...this.themes.default.sizes,
        ...themeOverrides.sizes
      },
      defaults: {
        ...this.themes.default.defaults,
        ...themeOverrides.defaults
      }
    };
  }
  static _ensureDefaultTheme() {
    if (!this._currentTheme.getValue() || !this._currentThemeKey.getValue()) {
      this._currentTheme.next(this.themes.default);
      this._currentThemeKey.next("default");
    }
  }
  static {
    this._currentThemeKey = new BehaviorSubject(null);
  }
  static get currentThemeKey() {
    this._ensureDefaultTheme();
    return this._currentThemeKey;
  }
  static {
    this._currentTheme = new BehaviorSubject(null);
  }
  static get currentTheme() {
    this._ensureDefaultTheme();
    return this._currentTheme;
  }
};
export {
  DesignSystem,
  breakPoints,
  spacing
};
